import { Fragment, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, XCircle, Loader2 } from 'lucide-react';

import { useSession } from '@/lib/useSession';
import { Pill } from '@/components/Pill';
import { gridTheadClassName } from '@ampersandbrands/ui';
import { formatDate, formatDateTime } from '@/lib/dateUtils';
import { DataGridShell } from '@/components/grid/DataGridShell';
import { hasFaireAdmin, hasFaireViewer, relativeTime } from '@/lib/useFaireRuns';
import {
  formatFreight,
  useShipmentBulkPush,
  useShipmentCounts,
  useShipmentPush,
  useShipments,
  type ShipmentPushStatus,
  type ShipmentRow,
} from '@/lib/useShipments';

const STATUS_FILTERS: Array<'eligible' | 'pushed' | 'failed' | 'suppressed' | 'all'> = [
  'eligible',
  'pushed',
  'failed',
  'suppressed',
  'all',
];

function statusPillClass(status: ShipmentPushStatus): string {
  if (status === 'pushed') {
    return 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300';
  }
  if (status === 'failed') {
    return 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300';
  }
  if (status === 'suppressed') {
    return 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300';
  }
  // eligible
  return 'bg-amp-cream-warm text-amp-ink dark:bg-amp-dark-surface-warm dark:text-amp-dark-ink';
}

function StatusPill({ status }: { status: ShipmentPushStatus }) {
  return <Pill tone={statusPillClass(status)}>{status}</Pill>;
}

function truncate(s: string | null | undefined, max: number): string {
  if (!s) return '';
  if (s.length <= max) return s;
  return `${s.slice(0, max)}...`;
}

function matchesSearch(row: ShipmentRow, q: string): boolean {
  if (!q) return true;
  const needle = q.toLowerCase();
  return (
    row.faire_order_id.toLowerCase().includes(needle) ||
    (row.retailer_name ?? '').toLowerCase().includes(needle) ||
    (row.tracking_number ?? '').toLowerCase().includes(needle)
  );
}

export function FaireShipments() {
  const { user } = useSession();
  const groups = user?.groups ?? [];
  const isViewer = hasFaireViewer(groups, user?.superAdmin);
  const isAdmin = hasFaireAdmin(groups, user?.superAdmin);

  const [status, setStatus] = useState<'eligible' | 'pushed' | 'failed' | 'suppressed' | 'all'>('eligible');
  const [since, setSince] = useState('');
  const [until, setUntil] = useState('');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [confirmBulk, setConfirmBulk] = useState(false);
  const [toast, setToast] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);

  const filters = useMemo(
    () => ({
      status,
      since: since || undefined,
      until: until || undefined,
      limit: 200,
      offset: 0,
    }),
    [status, since, until],
  );
  const shipmentsQ = useShipments(filters);
  const countsQ = useShipmentCounts();
  const pushOne = useShipmentPush();
  const pushBulk = useShipmentBulkPush();

  const rows = useMemo(
    () => shipmentsQ.shipments.filter((r) => matchesSearch(r, search)),
    [shipmentsQ.shipments, search],
  );

  if (!isViewer) {
    return <AccessDeniedCard />;
  }

  const toggleSelect = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const toggleExpanded = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const onPushRow = (faireOrderId: string) => {
    setToast(null);
    pushOne.mutate(faireOrderId, {
      onSuccess: (res) => {
        if (res.ok) {
          setToast({ kind: 'ok', text: `Pushed ${faireOrderId} to Faire.` });
        } else {
          setToast({
            kind: 'err',
            text: `Push failed for ${faireOrderId}: ${res.message ?? res.status_code ?? 'unknown error'}`,
          });
        }
      },
      onError: (err) => {
        setToast({ kind: 'err', text: `Push failed for ${faireOrderId}: ${err.message}` });
      },
    });
  };

  const onBulkPush = () => {
    if (selected.size === 0) return;
    setConfirmBulk(false);
    setToast(null);
    const ids = Array.from(selected);
    pushBulk.mutate(ids, {
      onSuccess: (res) => {
        setToast({
          kind: res.pushed === res.total ? 'ok' : 'err',
          text: `Pushed ${res.pushed} of ${res.total} shipments to Faire.`,
        });
        setSelected(new Set());
      },
      onError: (err) => {
        setToast({ kind: 'err', text: `Bulk push failed: ${err.message}` });
      },
    });
  };

  const selectableIds = rows
    .filter((r) => r.push_status === 'eligible' || r.push_status === 'failed')
    .map((r) => r.faire_order_id);
  const allSelected = selectableIds.length > 0 && selectableIds.every((id) => selected.has(id));

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelected(new Set());
    } else {
      setSelected(new Set(selectableIds));
    }
  };

  const toolbar = (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap gap-3">
        <div>
          <label htmlFor="status" className="block text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted mb-1">
            Status
          </label>
          <select
            id="status"
            value={status}
            onChange={(e) => setStatus(e.target.value as typeof status)}
            className="border border-amp-line rounded px-3 py-1.5 text-sm bg-amp-white text-amp-ink dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink"
          >
            {STATUS_FILTERS.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="since" className="block text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted mb-1">
            Since
          </label>
          <input
            id="since"
            type="date"
            value={since}
            onChange={(e) => setSince(e.target.value)}
            className="border border-amp-line rounded px-3 py-1.5 text-sm bg-amp-white text-amp-ink dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink"
          />
        </div>
        <div>
          <label htmlFor="until" className="block text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted mb-1">
            Until
          </label>
          <input
            id="until"
            type="date"
            value={until}
            onChange={(e) => setUntil(e.target.value)}
            className="border border-amp-line rounded px-3 py-1.5 text-sm bg-amp-white text-amp-ink dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink"
          />
        </div>
        <div className="flex-1 min-w-[200px]">
          <label htmlFor="search" className="block text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted mb-1">
            Search (order ID, retailer, tracking)
          </label>
          <input
            id="search"
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="F-2026-..."
            className="w-full border border-amp-line rounded px-3 py-1.5 text-sm bg-amp-white text-amp-ink placeholder:text-amp-ink-muted dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink dark:placeholder:text-amp-dark-ink-muted"
          />
        </div>
      </div>

      {isAdmin && selected.size > 0 && (
        <div className="flex items-center justify-between rounded-md border border-amp-line bg-amp-cream-warm px-3 py-2 dark:border-amp-dark-line dark:bg-amp-dark-surface-warm">
          <span className="text-sm text-amp-ink dark:text-amp-dark-ink">
            {selected.size} selected
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setSelected(new Set())}
              className="text-xs border border-amp-line rounded px-3 py-1 text-amp-ink hover:bg-amp-white dark:border-amp-dark-line dark:text-amp-dark-ink dark:hover:bg-amp-dark-surface"
            >
              Clear
            </button>
            <button
              type="button"
              onClick={() => setConfirmBulk(true)}
              disabled={pushBulk.isPending}
              className="text-xs border border-amp-ink bg-amp-ink text-amp-white rounded px-3 py-1 hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60 dark:border-amp-dark-ink dark:bg-amp-dark-ink dark:text-amp-dark-surface"
            >
              {pushBulk.isPending ? 'Pushing...' : `Push selected (${selected.size})`}
            </button>
          </div>
        </div>
      )}

      {confirmBulk && (
        <div
          className="rounded-md border border-amber-300 bg-amber-50 p-3 dark:border-amber-700 dark:bg-amber-900/30"
          role="dialog"
        >
          <p className="text-sm text-amp-ink dark:text-amp-dark-ink mb-2">
            Push tracking + freight to Faire for {selected.size} order
            {selected.size === 1 ? '' : 's'}? This is a live write to Faire and cannot
            be undone without contacting Faire support.
          </p>
          <ul className="text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted mb-3 max-h-32 overflow-y-auto">
            {Array.from(selected).map((id) => (
              <li key={id} className="font-mono">{id}</li>
            ))}
          </ul>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setConfirmBulk(false)}
              className="text-xs border border-amp-line rounded px-3 py-1 text-amp-ink hover:bg-amp-cream-warm dark:border-amp-dark-line dark:text-amp-dark-ink dark:hover:bg-amp-dark-surface-warm"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onBulkPush}
              className="text-xs border border-amp-ink bg-amp-ink text-amp-white rounded px-3 py-1 hover:opacity-90 dark:border-amp-dark-ink dark:bg-amp-dark-ink dark:text-amp-dark-surface"
            >
              Confirm push
            </button>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="flex h-full min-h-0 flex-col">
      <Link
        to="/sales/faire/dashboard"
        className="text-sm text-amp-ink-muted hover:text-amp-ink dark:text-amp-dark-ink-muted dark:hover:text-amp-dark-ink inline-flex items-center gap-1 mb-3 shrink-0"
      >
        <ArrowLeft className="h-3 w-3" /> Back to dashboard
      </Link>
      <div className="mb-4 shrink-0">
        <p className="text-xs uppercase tracking-wide text-amp-olive dark:text-amp-olive-dark mb-1">Faire</p>
        <h1 className="text-2xl font-semibold text-amp-ink dark:text-amp-dark-ink">Shipments</h1>
        <p className="text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">
          Sage invoices ready to push tracking + freight back to Faire.
        </p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-4 shrink-0">
        <CountTile
          label="Pending push"
          value={countsQ.isLoading ? '...' : String(countsQ.counts.eligible)}
          tone="default"
        />
        <CountTile
          label="Pushed today"
          value={countsQ.isLoading ? '...' : String(countsQ.counts.pushed_today)}
          tone="ok"
        />
        <CountTile
          label="Failed today"
          value={countsQ.isLoading ? '...' : String(countsQ.counts.failed_today)}
          tone={countsQ.counts.failed_today > 0 ? 'err' : 'default'}
        />
      </div>

      {toast && (
        <div
          className={`mb-4 shrink-0 rounded-md border px-3 py-2 text-sm ${
            toast.kind === 'ok'
              ? 'border-green-300 bg-green-50 text-green-800 dark:border-green-800 dark:bg-green-900/40 dark:text-green-200'
              : 'border-red-300 bg-red-50 text-red-800 dark:border-red-800 dark:bg-red-900/40 dark:text-red-200'
          }`}
          role="status"
        >
          {toast.text}
        </div>
      )}

      <DataGridShell
        loaded={rows.length}
        countNoun="shipments"
        toolbar={toolbar}
      >
        <table className="w-full text-sm">
          <thead className={gridTheadClassName}>
            <tr>
              {isAdmin && (
                <th className="text-left font-medium px-4 py-2 w-8">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={toggleSelectAll}
                    aria-label="Select all eligible"
                  />
                </th>
              )}
              <th className="text-left font-medium px-4 py-2">Faire order</th>
              <th className="text-left font-medium px-4 py-2">Retailer</th>
              <th className="text-left font-medium px-4 py-2">Ship date</th>
              <th className="text-left font-medium px-4 py-2">Tracking</th>
              <th className="text-left font-medium px-4 py-2">Carrier</th>
              <th className="text-right font-medium px-4 py-2">Freight</th>
              <th className="text-left font-medium px-4 py-2">First eligible</th>
              <th className="text-left font-medium px-4 py-2">Status</th>
              {isAdmin && <th className="text-left font-medium px-4 py-2">Action</th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-amp-line dark:divide-amp-dark-line">
            {shipmentsQ.isLoading ? (
              <tr>
                <td
                  colSpan={isAdmin ? 10 : 8}
                  className="px-4 py-6 text-amp-ink-muted dark:text-amp-dark-ink-muted"
                >
                  Loading shipments...
                </td>
              </tr>
            ) : shipmentsQ.error ? (
              <tr>
                <td colSpan={isAdmin ? 10 : 8} className="px-4 py-6 text-red-700 dark:text-red-300">
                  Could not load shipments: {shipmentsQ.error.message}
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td
                  colSpan={isAdmin ? 10 : 8}
                  className="px-4 py-6 text-amp-ink-muted dark:text-amp-dark-ink-muted"
                >
                  No shipments awaiting push. Sage invoices with tracking will appear here within
                  30 minutes of the next scanner run.
                </td>
              </tr>
            ) : (
              rows.map((row) => {
                const isRowExpanded = expanded.has(row.faire_order_id);
                const isPushable = row.push_status === 'eligible' || row.push_status === 'failed';
                return (
                  <Fragment key={row.faire_order_id}>
                    <tr className="hover:bg-amp-cream-warm dark:hover:bg-amp-dark-surface-warm">
                      {isAdmin && (
                        <td className="px-4 py-2">
                          <input
                            type="checkbox"
                            checked={selected.has(row.faire_order_id)}
                            disabled={!isPushable}
                            onChange={() => toggleSelect(row.faire_order_id)}
                            aria-label={`Select ${row.faire_order_id}`}
                          />
                        </td>
                      )}
                      <td className="px-4 py-2 font-mono text-xs text-amp-ink dark:text-amp-dark-ink">
                        {row.faire_order_id}
                      </td>
                      <td className="px-4 py-2 text-amp-ink dark:text-amp-dark-ink">
                        {row.retailer_name ?? '--'}
                      </td>
                      <td className="px-4 py-2 text-amp-ink-muted dark:text-amp-dark-ink-muted">
                        {formatDate(row.ship_date)}
                      </td>
                      <td
                        className="px-4 py-2 font-mono text-xs text-amp-ink dark:text-amp-dark-ink"
                        title={row.tracking_number ?? ''}
                      >
                        {truncate(row.tracking_number, 24)}
                      </td>
                      <td className="px-4 py-2 text-amp-ink dark:text-amp-dark-ink">
                        {row.carrier ?? '--'}
                      </td>
                      <td className="px-4 py-2 text-right text-amp-ink dark:text-amp-dark-ink">
                        {formatFreight(row.freight_amount)}
                      </td>
                      <td className="px-4 py-2 text-amp-ink-muted dark:text-amp-dark-ink-muted">
                        {relativeTime(row.first_eligible_at)}
                      </td>
                      <td className="px-4 py-2">
                        <button
                          type="button"
                          onClick={() => toggleExpanded(row.faire_order_id)}
                          className="inline-flex items-center gap-1"
                          aria-label="Toggle response details"
                        >
                          <StatusPill status={row.push_status} />
                          {row.push_status === 'failed' && row.retry_count > 0 && (
                            <span className="text-xs text-red-700 dark:text-red-300">
                              x{row.retry_count}
                            </span>
                          )}
                        </button>
                      </td>
                      {isAdmin && (
                        <td className="px-4 py-2">
                          {isPushable ? (
                            <button
                              type="button"
                              onClick={() => onPushRow(row.faire_order_id)}
                              disabled={pushOne.isPending}
                              className="text-xs border border-amp-line rounded px-2 py-1 text-amp-ink hover:bg-amp-cream-warm disabled:cursor-not-allowed disabled:opacity-60 dark:border-amp-dark-line dark:text-amp-dark-ink dark:hover:bg-amp-dark-surface-warm inline-flex items-center gap-1"
                            >
                              {pushOne.isPending && pushOne.variables === row.faire_order_id ? (
                                <Loader2 className="h-3 w-3 animate-spin" />
                              ) : row.push_status === 'failed' ? (
                                <XCircle className="h-3 w-3 text-red-600 dark:text-red-400" />
                              ) : (
                                <CheckCircle2 className="h-3 w-3" />
                              )}
                              {row.push_status === 'failed' ? 'Retry' : 'Push'}
                            </button>
                          ) : (
                            <span className="text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted">--</span>
                          )}
                        </td>
                      )}
                    </tr>
                    {isRowExpanded && (row.faire_response_body || row.faire_response_code) && (
                      <tr className="bg-amp-cream-warm dark:bg-amp-dark-surface-warm">
                        <td colSpan={isAdmin ? 10 : 8} className="px-4 py-2 text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted">
                          <div className="font-mono whitespace-pre-wrap break-all">
                            <span className="font-medium">HTTP {row.faire_response_code ?? '--'}:</span>{' '}
                            {row.faire_response_body ?? '(no body)'}
                          </div>
                          {row.pushed_by && (
                            <div className="mt-1">
                              Pushed by <span className="font-mono">{row.pushed_by}</span>{' '}
                              {row.pushed_at && `at ${formatDateTime(row.pushed_at)}`}
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </DataGridShell>

      {!isAdmin && (
        <p className="text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted mt-2 shrink-0">
          Push is admin-only; contact IT for the cockpit-faire-sage-admin role.
        </p>
      )}
    </div>
  );
}

function CountTile({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone: 'default' | 'ok' | 'err';
}) {
  const toneClass =
    tone === 'ok'
      ? 'text-green-700 dark:text-green-300'
      : tone === 'err'
        ? 'text-red-700 dark:text-red-300'
        : 'text-amp-ink dark:text-amp-dark-ink';
  return (
    <div className="border border-amp-line rounded-lg bg-amp-white p-4 dark:border-amp-dark-line dark:bg-amp-dark-surface">
      <div className="text-xs uppercase tracking-wide text-amp-olive dark:text-amp-olive-dark">{label}</div>
      <div className={`text-2xl font-semibold mt-1 ${toneClass}`}>{value}</div>
    </div>
  );
}

function AccessDeniedCard() {
  return (
    <div>
      <div className="mb-6">
        <p className="text-xs uppercase tracking-wide text-amp-olive dark:text-amp-olive-dark mb-1">Faire</p>
        <h1 className="text-2xl font-semibold text-amp-ink dark:text-amp-dark-ink">Shipments</h1>
      </div>
      <div className="border border-amp-line rounded-lg bg-amp-white p-6 dark:border-amp-dark-line dark:bg-amp-dark-surface">
        <h2 className="text-[16px] font-semibold text-amp-ink dark:text-amp-dark-ink mb-2">You don't have access to Faire-Sage.</h2>
        <p className="text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">
          Contact IT to request the cockpit-faire-sage-viewer role.
        </p>
      </div>
    </div>
  );
}
